import React, { useEffect, useMemo, useState } from 'react'
import {
  Home as IconHome,
  MapPin as IconLocation,
  PlusCircle as IconPlusCircle,
  Plus as IconPlus,
  X as IconX,
  ArrowLeft as IconArrowLeft,
  Check as IconCheck,
  AlertCircle as IconAlertCircle,
} from 'react-feather'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import styled from 'styled-components'
import { Modal, Row, CustomInput } from 'reactstrap'

import Breadcrumb from '~/components/Breadcrumb'
import { Col } from '~/components/commons'
import { FormattedMessage, useIntl } from 'react-intl'
import useAxios from 'axios-hooks'
import {
  CustomTable,
  TableRow,
  Content,
  Avatar,
} from '~/components/ecto/CustomTable'
import { Show } from '~components/commons/display'
import { useSelector } from 'react-redux'
import { ButtonCreateMobile, BtnCreateMobile } from '~/components/ecto/commons'
import { ADMIN, SUPER_ADMIN } from '~/constants/roles'
import Select from 'react-select'
import MultiSelect from '~/components/MultiSelect'
import {
  defaultUserPermissions,
  defaultUserPermissionIcons,
} from '~/constants/users'
import { TableContainer } from './commons'
import { toast } from 'react-toastify'

const simpleEmailRegex = /.{1,}@.{1,}\..{1,}/g

const USER_ACTIVATION_STATUS = {
  true: {
    false: 'Activated',
    true: 'Activated',
  },
  false: {
    false: 'Deactivated',
    true: 'Pending Invite',
  },
}

const USER_ACTIVATION_CLASSNAME = {
  true: {
    false: 'success',
    true: 'success',
  },
  false: {
    false: 'danger',
    true: 'warning',
  },
}

function PermissionsTable({
  userPermission,
  userIsAppAdmin,
  userIsAdmin,
  isSelf = false,
  value,
  setValue,
}) {
  const formPermissions = value
  const permissions = [...defaultUserPermissions].filter(permissionKey => {
    return userIsAppAdmin || userIsAdmin || userPermission[permissionKey]
  })

  return (
    <div style={{ flexGrow: '1' }}>
      <TableContainer style={{ marginTop: '10px' }}>
        <div className="table-responsive">
          <table className="table table-borderless table-striped mb-0">
            <tbody>
              {permissions?.map((permissionKey, index) => {
                const Icon = defaultUserPermissionIcons[permissionKey]
                return (
                  <tr key={index}>
                    <td
                      className="pl-2"
                      style={{
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        fontWeight: 500,
                        fontFamily: 'Montserrat',
                      }}
                    >
                      <div
                        className="d-flex w-100 h-100 align-items-center"
                        style={{ gap: '1em' }}
                      >
                        <Icon size="20" />
                        <FormattedMessage id={_.upperFirst(permissionKey)} />
                      </div>
                    </td>

                    {!isSelf && (
                      <td key={index}>
                        <div className="custom-checkbox custom-control d-flex justify-content-end">
                          <CustomInput
                            type="switch"
                            id={`user-userPermission-${permissionKey}`}
                            name={`user.userPermission.${permissionKey}`}
                            checked={formPermissions[permissionKey]}
                            onChange={() =>
                              setValue({
                                ...value,
                                [permissionKey]: !value[permissionKey],
                              })
                            }
                            inline
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </TableContainer>
    </div>
  )
}

function ListUsers() {
  const intl = useIntl()
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [relationType, setRelationType] = useState('PROJECT')
  const [selectedAccount, setSelectedAccount] = useState('')
  const [accountDisabled, setAccountDisabled] = useState(false)
  const [projectIds, setProjectIds] = useState([])
  const [locationIds, setLocationIds] = useState([])
  const [permissions, setPermissions] = useState({})
  const [formPage, setFormPage] = useState(1)
  const [inviteType, setInviteType] = useState('INDIVIDUAL')
  const [emailList, setEmailList] = useState([
    {
      email: '',
      role: { value: 0, label: intl.formatMessage({ id: 'Client' }) },
    },
  ])
  const [emailText, setEmailText] = useState('')
  const userProfile = useSelector(state => state.user?.profile)
  const hasAppAdminPermission = userProfile?.role === SUPER_ADMIN
  const accountId = useSelector(state => state.user?.profile.account.id)
  const profileAccount = useSelector(state => state?.user?.profile?.account?.id)
  const profileAccountName = useSelector(
    state => state?.user?.profile?.account?.name
  )
  const userIsAppAdmin = userProfile?.role === SUPER_ADMIN
  const userIsAdmin = userProfile?.role === ADMIN
  const userPermission = userProfile?.userPermission

  const usersUrl = hasAppAdminPermission
    ? '/users'
    : `/accounts/${accountId}/users`

  function useOptions(endpoint, getData, buildOption, config) {
    const [requestState, refetch] = useAxios(endpoint, config)

    const options = useMemo(() => {
      const { data, loading, error } = requestState
      if (!data || loading || error) {
        return []
      }

      return getData(data)?.map(item => buildOption(item))
    }, [requestState, getData, buildOption])
    return [options, refetch, requestState]
  }

  const [{ data, loading, error }] = useAxios(usersUrl, { useCache: false })

  const [projects, , { loading: projectLoad }] = useOptions(
    userIsAppAdmin
      ? `/accounts/${selectedAccount.value}/projects/options`
      : `/accounts/${profileAccount}/projects/options`,
    data => data.projects,
    project => ({
      label: project.name,
      value: [project.id],
    }),
    {
      useCache: false,
      manual: userIsAppAdmin ? !selectedAccount : false,
    }
  )

  const [locations, , { loading: locationLoad }] = useOptions(
    userIsAppAdmin
      ? `/accounts/${selectedAccount.value}/locations/options`
      : `/accounts/${profileAccount}/locations/options`,
    data => data.locations,
    location => ({
      label: location,
      value: [location.id],
    }),
    {
      useCache: false,
      manual: userIsAppAdmin ? !selectedAccount : false,
    }
  )

  const [accounts, , { loading: accountLoad }] = useOptions(
    userIsAppAdmin ? '/admin/accounts' : `/accounts/${profileAccount}`,
    data => (userIsAppAdmin ? data : [data]),
    account => ({
      label: account.name,
      value: account.id,
    }),
    { useCache: false }
  )

  const [, submitUserInvite] = useAxios(
    {
      method: 'POST',
      url: '/users/invite',
    },
    { useCache: false, manual: true }
  )

  const users = useMemo(() => {
    if (!data || loading || error) {
      return []
    }

    return _.sortBy(data, [value => (value.deletedAt ? 1 : 0)])
  }, [data, error, loading])

  const countLocations = user => {
    const locations = user?.projects?.reduce((count, project) => {
      return count + (project.locations?.length || 0)
    }, 0)

    return locations
  }

  const filteredUsers = useMemo(() => {
    const filtered = !_.isEmpty(search)
      ? users.filter(
          el =>
            el.name.toLowerCase().includes(search) ||
            el.email.toLowerCase().includes(search)
        )
      : users

    return filtered.map(user => ({
      ...user,
      totalLocationCount: countLocations(user),
    }))
  }, [search, users])

  const headers = useMemo(() => {
    const tableHeaders = [
      { name: 'User', width: '40%', style: { paddingLeft: '30px' } },
      { name: 'Status', center: true },
      { name: 'Locations', center: true },
      { name: 'Account', center: true },
      { name: '_blank', center: true, width: '105px' },
    ]

    if (!hasAppAdminPermission) {
      tableHeaders.forEach(header => {
        if (header.name === 'Account') {
          tableHeaders.splice(tableHeaders.indexOf(header), 1)
        }
      })
    }

    return tableHeaders
  }, [hasAppAdminPermission])

  function closeModal() {
    setIsOpen(false)

    setFormPage(1)
    setRelationType('PROJECT')
    setEmailList([
      {
        email: '',
        role: { value: 0, label: intl.formatMessage({ id: 'Client' }) },
      },
    ])
    setEmailText('')
    setProjectIds([])
    setLocationIds([])
    setPermissions({})

    !userIsAppAdmin
      ? setSelectedAccount({
          label: profileAccountName,
          value: profileAccount,
        })
      : setSelectedAccount('')
  }

  function gotoPageTwo() {
    if (selectedAccount) setFormPage(2)
  }

  function handleAddEmail() {
    const lastRole = emailList.at(-1).role
    const newEmailList = [...emailList, { email: '', role: lastRole }]
    setEmailList(newEmailList)
  }

  function handleInviteType() {
    setEmailList([
      {
        email: '',
        role: { value: 0, label: intl.formatMessage({ id: 'Client' }) },
      },
    ])
    setEmailText('')
    setInviteType(inviteType === 'INDIVIDUAL' ? 'BULK' : 'INDIVIDUAL')
  }

  async function handleSubmit() {
    setIsLoading(true)
    const selectedProjects = projectIds.map(project => project.value[0])
    const selectedLocations = locationIds.map(location => location.value[0])
    const hasNoEmails =
      (inviteType === 'INDIVIDUAL' && !emailList.length) ||
      (inviteType === 'BULK' &&
        !emailText
          .split('\n')
          .filter(email => email.trim().match(simpleEmailRegex)).length)
    const hasNoRelations =
      (relationType === 'PROJECT' && !selectedProjects.length) ||
      (relationType === 'LOCATION' && !selectedLocations.length)

    if (hasNoEmails) {
      return toast.error('Please add at least one email')
    }

    if (hasNoRelations) {
      toast.error('Please select at least one project or location')
    }

    const data = {
      account: selectedAccount.value,
      relationType,
      ...(relationType === 'PROJECT' && { projectIds: selectedProjects }),
      ...(relationType === 'LOCATION' && { locationIds: selectedLocations }),
      permissions,
      users:
        inviteType === 'INDIVIDUAL'
          ? emailList.map(({ email, role }) => ({ email, role: role.value }))
          : emailText
              .split('\n')
              .filter(email => email.trim().match(simpleEmailRegex))
              .map(email => ({ email, role: 0 })),
    }

    await submitUserInvite({
      data,
    })
      .then(res => {
        const hasErrors = !!res.data.emailsWithError.length
        closeModal()
        if (hasErrors) {
          toast.success(
            intl.formatMessage({
              id: 'Some invites sent successfully\ninvalid emails:\n',
            }) + res.data.emailsWithError.join('\n')
          )
        } else {
          toast.success(
            intl.formatMessage({ id: 'All invites sent succesfully' })
          )
        }
        setIsLoading(false)
      })
      .catch(error => {
        if (error?.response?.data?.emailWithErrors?.length) {
          toast.error(
            intl.formatMessage({
              id:
                "All emails were invalid, they are already registered or don't exist",
            })
          )
        } else {
          toast.error(
            intl.formatMessage({
              id: 'An error ocurred while sending the invites',
            })
          )
        }
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (!userIsAppAdmin) {
      setSelectedAccount({
        label: profileAccountName,
        value: profileAccount,
      })
      setAccountDisabled(true)
    } else {
      setAccountDisabled(false)
    }
  }, [profileAccount, profileAccountName, userIsAppAdmin])

  return (
    <Col>
      <Modal isOpen={isOpen} className="modal-lg">
        <ModalDiv>
          <div className="invite-modal-body">
            <div className="invite-modal-header">
              <div>
                <button className="circle-logo">
                  <IconArrowLeft
                    color="#718096"
                    size="20"
                    onClick={() =>
                      formPage === 1 ? closeModal() : setFormPage(1)
                    }
                  />
                </button>
                <div>
                  <FormattedMessage id="Invite people" />
                </div>
              </div>
              <div>
                <button>
                  <IconX color="#2D3748" size="20" onClick={closeModal} />
                </button>
              </div>
            </div>
            <hr />
            <div className="invite-form-timeline">
              <div className="invite-line"></div>
              <div className="invite-timeline-info">
                <div>
                  <div
                    className="invite-circle-progress"
                    data-status={
                      formPage > 1 ? 'completed' : formPage === 1 && 'active'
                    }
                    onClick={() => setFormPage(1)}
                  >
                    {formPage > 1 ? <IconCheck size="15" /> : 1}
                  </div>
                  <div>
                    <FormattedMessage id="Configuration" />
                  </div>
                </div>
                <div>
                  <div
                    className="invite-circle-progress"
                    data-status={
                      formPage > 2 ? 'completed' : formPage === 2 && 'active'
                    }
                    onClick={gotoPageTwo}
                  >
                    2
                  </div>
                  <div>
                    <FormattedMessage id="Invites" />
                  </div>
                </div>
              </div>
            </div>
            {formPage === 1 && (
              <>
                <div className="invite-section">
                  <div className="invite-heading">
                    <FormattedMessage id="Access" />
                  </div>
                  <div>
                    <Row>
                      <Col md="6" className="px-1 mb-1">
                        <label>
                          <FormattedMessage id="Vinculate Account" />
                        </label>
                        <Select
                          className="basic-single"
                          isDisabled={accountDisabled}
                          classNamePrefix="select"
                          isLoading={accountLoad}
                          value={selectedAccount}
                          placeholder={
                            <FormattedMessage id="Select the account" />
                          }
                          onChange={value => setSelectedAccount(value)}
                          options={accounts}
                        />
                      </Col>
                      <Col md="6" className="px-1">
                        <RadioSelect>
                          <div>
                            <input
                              type="radio"
                              id="project"
                              name="type"
                              value="PROJECT"
                              onChange={e => setRelationType(e.target.value)}
                              checked={relationType === 'PROJECT'}
                            />
                            <label htmlFor="project">
                              <FormattedMessage id="Project(s)" />
                            </label>
                          </div>
                          <div>
                            <input
                              type="radio"
                              id="location"
                              name="type"
                              value="LOCATION"
                              onChange={e => setRelationType(e.target.value)}
                              checked={relationType === 'LOCATION'}
                            />
                            <label htmlFor="location">
                              <FormattedMessage id="Location(s)" />
                            </label>
                          </div>
                        </RadioSelect>
                        {relationType === 'PROJECT' ? (
                          <MultiSelect
                            uniqueKey="projectsIds"
                            className="basic-single"
                            classNamePrefix="select"
                            isDisabled={!selectedAccount && userIsAppAdmin}
                            menuPortalTarget={undefined}
                            styles={{
                              menu: baseStyles => ({
                                ...baseStyles,
                                zIndex: 10,
                              }),
                            }}
                            placeholder={<FormattedMessage id="All projects" />}
                            options={projects}
                            loading={projectLoad}
                            value={projectIds}
                            onChange={options => setProjectIds(options)}
                          />
                        ) : (
                          <MultiSelect
                            uniqueKey="locationIds"
                            className="basic-single"
                            classNamePrefix="select"
                            isDisabled={!selectedAccount && userIsAppAdmin}
                            menuPortalTarget={undefined}
                            placeholder={
                              <FormattedMessage id="All locations" />
                            }
                            options={locations}
                            loading={locationLoad}
                            value={locationIds}
                            styles={{
                              menu: baseStyles => ({
                                ...baseStyles,
                                zIndex: 10,
                              }),
                            }}
                            filterOption={(candidate, input) => {
                              const normalizedCandidate = candidate.label.name
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .toLowerCase()
                              const normalizedInput = input
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .toLowerCase()
                              const normalizedAddress = candidate.label.data.storefrontAddress?.addressLines?.[0]
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .toLowerCase()

                              return (
                                normalizedCandidate.includes(normalizedInput) ||
                                normalizedAddress.includes(normalizedInput)
                              )
                            }}
                            onChange={options => setLocationIds(options)}
                            renderOption={({ label: location }) => (
                              <div className="col">
                                <span>{location?.name ?? ''}</span>
                                <small
                                  style={{ display: 'block', color: '#8D8D8D' }}
                                >
                                  {location?.data?.storefrontAddress?.addressLines?.join(
                                    ' '
                                  )}
                                  ,{' '}
                                  {location?.data?.storefrontAddress?.locality}{' '}
                                  -{' '}
                                  {
                                    location?.data?.storefrontAddress
                                      ?.administrativeArea
                                  }
                                </small>
                              </div>
                            )}
                          />
                        )}
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="invite-section">
                  <div className="invite-heading">
                    <FormattedMessage id="Permissions" />
                  </div>
                  <div>
                    <PermissionsTable
                      userIsAppAdmin={userIsAppAdmin}
                      userIsAdmin={userIsAdmin}
                      userPermission={userPermission}
                      value={permissions}
                      setValue={setPermissions}
                    />
                  </div>
                </div>
                <hr />
                <div className="invite-button-footer">
                  <button onClick={gotoPageTwo} disabled={!selectedAccount}>
                    <FormattedMessage id="Proceed" />
                  </button>
                </div>
              </>
            )}
            {formPage === 2 && (
              <>
                {inviteType === 'INDIVIDUAL' && (
                  <div className="invite-section">
                    <div className="invite-section-individual">
                      {emailList.map((emailInput, index) => (
                        <div className="invite-individual-item" key={index}>
                          <input
                            value={emailList[index].email}
                            placeholder={intl.formatMessage({
                              id: 'email@company.com',
                            })}
                            onChange={e => {
                              const newEmailList = [...emailList]
                              newEmailList[index].email = e.target.value
                              setEmailList(newEmailList)
                            }}
                          />
                          <Select
                            className="basic-single"
                            classNamePrefix="select"
                            value={emailList[index].role}
                            onChange={value => {
                              const newEmailList = [...emailList]
                              newEmailList[index].role = value
                              setEmailList(newEmailList)
                            }}
                            styles={{
                              control: base => ({
                                ...base,
                                borderRadius: '0 4px 4px 0',
                              }),
                            }}
                            options={[
                              {
                                value: 0,
                                label: intl.formatMessage({ id: 'Client' }),
                              },
                              {
                                value: 1,
                                label: intl.formatMessage({
                                  id: 'Client Admin',
                                }),
                              },
                            ]}
                          />
                        </div>
                      ))}
                    </div>
                    <div>
                      <button
                        className="invite-email-button"
                        onClick={handleAddEmail}
                        disabled={
                          !emailList.at(-1).email.trim().match(simpleEmailRegex)
                        }
                      >
                        <IconPlus
                          size="15"
                          color={
                            !emailList
                              .at(-1)
                              .email.trim()
                              .match(simpleEmailRegex)
                              ? '#d3d3d3'
                              : '#6236FF'
                          }
                        />
                        <FormattedMessage id="Add another email" />
                      </button>
                    </div>
                  </div>
                )}
                {inviteType === 'BULK' && (
                  <div className="invite-section">
                    <div className="invite-section-bulk">
                      <div className="bulk-header">
                        <FormattedMessage id="Add one email per line" />
                        <div>
                          <IconAlertCircle size="20" color="#d3d3d3" />
                          <FormattedMessage id="You can copy emails from a spread sheet directly to this input" />
                        </div>
                      </div>
                      <div className="bulk-input">
                        <textarea
                          value={emailText}
                          onChange={e => {
                            setEmailText(e.target.value.replaceAll('\t', '\n'))
                          }}
                        />
                      </div>
                      <div className="bulk-footer">
                        {
                          emailText
                            .split('\n')
                            .filter(email =>
                              email.trim().match(simpleEmailRegex)
                            ).length
                        }{' '}
                        <FormattedMessage id="emails added" />
                      </div>
                    </div>
                  </div>
                )}
                <hr />
                <div className="invite-button-footer">
                  {inviteType === 'INDIVIDUAL' && (
                    <button
                      disabled={isLoading}
                      className="secondary"
                      onClick={handleInviteType}
                    >
                      <FormattedMessage id="Bulk input" />
                    </button>
                  )}
                  {inviteType === 'BULK' && (
                    <button
                      disabled={isLoading}
                      className="secondary"
                      onClick={handleInviteType}
                    >
                      <FormattedMessage id="Individual input" />
                    </button>
                  )}
                  <button
                    disabled={
                      (inviteType === 'BULK'
                        ? !emailText
                            .split('\n')
                            .filter(email =>
                              email.trim().match(simpleEmailRegex)
                            ).length
                        : !emailList.filter(({ email }) =>
                            email.trim().match(simpleEmailRegex)
                          ).length) || isLoading
                    }
                    onClick={handleSubmit}
                  >
                    <FormattedMessage id="Send invites" />
                  </button>
                </div>
              </>
            )}
          </div>
        </ModalDiv>
      </Modal>
      <BreadcrumbContainer>
        <Breadcrumb
          className="mb-2"
          paths={[
            {
              render: () => (
                <Link className="d-flex align-items-center" to="/users">
                  <IconHome size={20} />
                </Link>
              ),
            },
            { text: 'Users' },
          ]}
        />
        <BreadcrumbButtons>
          <BtnCreateMobile onClick={() => setIsOpen(true)}>
            <IconPlusCircle height={14} className="d-none d-md-block" />
            <IconPlus className="d-md-none d-block" />
            <FormattedMessage id="Invite user" />
          </BtnCreateMobile>
          <ButtonCreateMobile to="/users/new">
            <IconPlusCircle height={14} className="d-none d-md-block" />
            <IconPlus className="d-md-none d-block" />
            <FormattedMessage id="Create user" />
          </ButtonCreateMobile>
        </BreadcrumbButtons>
      </BreadcrumbContainer>
      <CustomTable
        search
        searchPlaceholder="Search users"
        setSearch={setSearch}
        searchValue={search}
        headers={headers}
      >
        {filteredUsers?.map(user => (
          <TableRow key={user.id}>
            <Content>
              <div className="d-flex">
                <Avatar avatar={user.avatar} />
                <div className="user-info">
                  <h6>
                    {user.name === '_INVITED_USER_'
                      ? intl.formatMessage({ id: 'Invited User' })
                      : user.name}
                  </h6>
                  <span>{user.email}</span>
                </div>
              </div>
            </Content>
            <Content
              text={
                USER_ACTIVATION_CLASSNAME[!user.deletedAt && user.isActive][
                  user.name === '_INVITED_USER_'
                ]
              }
            >
              {intl.formatMessage({
                id:
                  USER_ACTIVATION_STATUS[!user.deletedAt && user.isActive][
                    user.name === '_INVITED_USER_'
                  ],
              })}
            </Content>
            <Content>
              <IconLocation className="mr-1" />
              {user.locationsCount || 0}
            </Content>
            <Show
              condition={hasAppAdminPermission}
              // eslint-disable-next-line react/no-children-prop
              children={<Content>{user?.accountName}</Content>}
            />
            <Content>
              {userProfile?.role >= user.role && (
                <Link to={`/users/${user.id}`}>
                  <FormattedMessage id="View" />
                </Link>
              )}
            </Content>
          </TableRow>
        ))}
      </CustomTable>
    </Col>
  )
}

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const RadioSelect = styled.div`
  display: flex;
  height: 20px;
  gap: 1em;
  accent-color: #7367f0;

  div {
    display: flex;
  }
`

const BreadcrumbButtons = styled.div`
  display: flex;
  gap: 1rem;
`

const ModalDiv = styled.div`
  .invite-modal-body {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    width: 100%;
  }

  .circle-logo {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #edf2f7;
    border-radius: 999999px;
    aspect-ratio: 1/1;
  }

  .invite-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    div:nth-child(1) {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 1rem;

      div {
        color: #171923;
        font-size: 1.25rem;
        font-weight: 700;
      }
    }
  }

  .invite-heading {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c2c2c;
  }

  .invite-button-footer {
    width: 100%;
    display: flex;
    justify-content: end;
    gap: 1rem;

    button {
      padding: 0.625rem 1.375rem;
      background-color: #61c478;
      color: #fff;
      font-weight: 700;
      border-radius: 0.375rem;

      &:disabled {
        background-color: #d3d3d3;
        color: #fff;
        cursor: not-allowed;
      }
    }

    .secondary {
      background-color: transparent;
      color: #979797 !important;
      border: 1px solid #979797;
    }
  }

  .invite-form-timeline {
    position: relative;
    display: flex;
    flex-direction: column;

    .invite-line {
      width: 100%;
      border-top: 1px solid #d3d3d3;
      margin: 1rem 0;
    }

    .invite-circle-progress {
      position: absolute;
      top: 0;
      height: 2rem;
      display: flex;
      justify-content: center;
      align-items: center;
      aspect-ratio: 1/1;
      border-radius: 99999px;
      background-color: #d3d3d3;
      color: #343a40;
      font-weight: 700;
      line-height: 2rem;
      cursor: pointer;

      &[data-status='active'] {
        background-color: #556ee6;
        color: #fff;
      }

      &[data-status='completed'] {
        background-color: #34c38f;
        color: #fff;
      }
    }

    .invite-timeline-info {
      display: flex;
      flex-direction: row;

      > div {
        display: flex;
        flex-direction: row;
        justify-content: start;
        align-items: center;
        color: #343a40;
        font-size: 0.8rem;
        font-weight: 700;
        width: 50%;
        margin-top: 0.5rem;
      }
    }
  }

  .invite-section-bulk {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;

    .bulk-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      font-weight: 700;
      color: #000;

      > div {
        width: 50%;
        display: flex;
        gap: 0.5rem;
        align-items: center;
        color: #d3d3d3;
        font-size: 0.75rem;
      }
    }

    .bulk-input {
      textarea {
        width: 100%;
        height: 10rem;
        padding: 0.5rem;
        border: 1px solid #d3d3d3;
        border-radius: 0.375rem;
        resize: none;
      }
    }
  }

  .invite-section-individual {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;

    .invite-individual-item {
      display: flex;
      flex-direction: row;

      input {
        width: 70%;
        border: 1px solid #d3d3d3;
        border-radius: 0.375rem 0 0 0.375rem;
        padding: 0.5rem;
      }

      > div {
        width: 30%;
      }
    }
  }

  .invite-email-button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    background-color: transparent;
    color: #6236ff;

    &:disabled {
      color: #d3d3d3;
      cursor: not-allowed;
    }
  }

  hr {
    width: 100%;
  }

  button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

export default ListUsers
